import {
  EPermissionGroup,
  IPermissions
} from 'common/permissions.types';

import { EPublisherSolutionModel } from '../common/contracts';
import { EFeatureFlag, ELocalStorageKeys } from '../constants/enums';

import { localStorageUtil } from './localStorageUtil';

type PermissionPredicate = (permission: EPermissionGroup) => boolean;

export type FeatureFlaggedPermissions = Partial<
  Record<EPermissionGroup, EFeatureFlag>
>;

export const buildPermissionsMatcher =
  (requiredPermissions: EPermissionGroup[], matchAnyPermission?: boolean) =>
  (isPermissionGranted: PermissionPredicate): boolean => {
    if (matchAnyPermission) {
      return requiredPermissions.some(isPermissionGranted);
    }
    return requiredPermissions.every(isPermissionGranted);
  };

export const resolveFeatureFlaggedPermissions = (
  permissions: EPermissionGroup[],
  featureFlaggedPermissions?: FeatureFlaggedPermissions,
  featureFlags?: Record<EFeatureFlag, unknown>
): EPermissionGroup[] => {
  if (!featureFlaggedPermissions) {
    return permissions;
  }
  return permissions.filter((permission) => {
    const gatingFeatureFlag = featureFlaggedPermissions[permission];
    if (!gatingFeatureFlag) {
      return true;
    }
    return Boolean(featureFlags?.[gatingFeatureFlag]);
  });
};

export const permissionsUtil = {
  isCheckout: () => {
    const solutionModel = localStorageUtil.getAny<EPublisherSolutionModel>(
      ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL
    );
    return solutionModel === EPublisherSolutionModel.CHECKOUT;
  },
  shouldHideGroupInPermission: (
    permissions: IPermissions | undefined,
    group: EPermissionGroup,
    activeProject: string | undefined
  ) => {
    if (!permissions) {
      return false;
    }
    return !(permissions && activeProject
      ? permissions?.[activeProject]?.[group]
      : false);
  },
  canAccessSettings: (
    permissions: IPermissions | undefined,
    activeProject: string | undefined
  ) => {
    if (!activeProject) return true;
    return (
      permissions?.[activeProject]?.[EPermissionGroup.COMPANY] ||
      permissions?.[activeProject]?.[EPermissionGroup.USERS] ||
      permissions?.[activeProject]?.[EPermissionGroup.AUTHENTICATION]
    );
  },
  canAccessAnalytics: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.ANALYTICS];
  },
  canAccessApiPerformance: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.SLA_PERFORMANCE_DASHBOARD];
  },
  canAccessBuilder: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.BUILDER];
  },
  canAccessUsers: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.USERS];
  },
  canAccessIntegration: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.INTEGRATION];
  },
  canAccessProfiles: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.PROFILES];
  },
  canAccessPublisher: (permissions: IPermissions, project: string) => {
    if (!permissions) return false;
    return permissions?.[project]?.[EPermissionGroup.COMPANY];
  },
  getPublishersByPayouts: (permissions: IPermissions) => {
    if (!permissions) return [];
    return Object.keys(permissions).filter(
      (key) => permissions[key][EPermissionGroup.PAYOUTS]
    );
  },
  getAccessPublisherOrders: (permissions: IPermissions) => {
    if (!permissions) return [];
    return Object.keys(permissions).filter(
      (key) => permissions[key][EPermissionGroup.ORDERS]
    );
  },
  getAccessPublisherReports: (permissions: IPermissions) => {
    if (!permissions) return [];
    return Object.keys(permissions).filter(
      (key) => permissions[key][EPermissionGroup.FINANCIAL_REPORT]
    );
  },
  getPublishersByFinancialReports: (permissions: IPermissions) => {
    if (!permissions) return [];
    return Object.keys(permissions).filter(
      (key) =>
        permissions[key][EPermissionGroup.FINANCIAL_REPORT] &&
        permissions[key][EPermissionGroup.PAYOUTS]
    );
  },
  isAnyOrdersOrPayoutsEnabled: (permissions: IPermissions) => {
    if (!permissions) return false;
    return Object.keys(permissions).some(
      (key) =>
        permissions[key][EPermissionGroup.ORDERS] ||
        permissions[key][EPermissionGroup.PAYOUTS]
    );
  }
};
